import { Injectable } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';

export interface DialogLayoutBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** localStorage keys — cleared on logout (`clearSignedInStorage`) */
export const FLOATING_DIALOG_LAYOUT_KEYS = {
  aiAssistant: 'medicai.floatingDialog.aiAssistant',
  // The billing pre-export review wizard. Its own key, not the assistant's: the two are open for
  // different tasks and a biller who has sized one to sit beside the billing list should not have
  // that undone by the assistant's last position.
  icdReviewWizard: 'medicai.floatingDialog.icdReviewWizard',
  // The CURA form review. Its own key: it is opened over the chat and sized to show the
  // patient file beside it, which is not where the chat sits.
  curaFormReview: 'medicai.floatingDialog.curaFormReview',
} as const;

const MIN_W = 280;
const MIN_H = 200;

@Injectable({
  providedIn: 'root'
})
export class FloatingDialogLayoutService {
  load(key: string): DialogLayoutBounds | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return null;
      }
      const b = JSON.parse(raw) as DialogLayoutBounds;
      if (
        [b.left, b.top, b.width, b.height].some(
          (n) => typeof n !== 'number' || !isFinite(n) || n <= 0
        )
      ) {
        return null;
      }
      return this.clampToViewport({
        left: Math.round(b.left),
        top: Math.round(b.top),
        width: Math.round(b.width),
        height: Math.round(b.height),
      });
    } catch {
      return null;
    }
  }

  save(key: string, bounds: DialogLayoutBounds): void {
    const b = this.clampToViewport({
      left: Math.round(bounds.left),
      top: Math.round(bounds.top),
      width: Math.round(bounds.width),
      height: Math.round(bounds.height),
    });
    localStorage.setItem(key, JSON.stringify(b));
  }

  /** Read overlay pane position/size and persist */
  saveFromPane(key: string, pane: HTMLElement): void {
    const rect = pane.getBoundingClientRect();
    this.save(key, {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }

  clampToViewport(b: DialogLayoutBounds): DialogLayoutBounds {
    const margin = 8;
    const maxW = Math.max(MIN_W, window.innerWidth - margin * 2);
    const maxH = Math.max(MIN_H, window.innerHeight - margin * 2);
    const width = Math.min(Math.max(b.width, MIN_W), maxW);
    const height = Math.min(Math.max(b.height, MIN_H), maxH);
    let left = b.left;
    let top = b.top;
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
    return { left, top, width, height };
  }

  getMatDialogOpenOptions(
    key: string,
    defaults: { width: string; height: string }
  ): Pick<MatDialogConfig, 'width' | 'height' | 'position'> {
    const b = this.load(key);
    if (!b) {
      return { width: defaults.width, height: defaults.height };
    }
    return {
      width: `${b.width}px`,
      height: `${b.height}px`,
      position: { left: `${b.left}px`, top: `${b.top}px` },
    };
  }
}
